<template>
    <div v-show="showing" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div v-if="job" class="bg-white rounded-lg shadow-lg w-96 p-6">
        <h2 class="text-2xl font-bold mb-4">Job: {{ job.name }}</h2>
        <p class="text-gray-700 mb-4">{{ job.description }}</p>
        <div class="mb-4">
          <p class="text-sm text-gray-600">
            Job time: {{ job.start }} - {{ job.end }} (Duration: {{ (job.duration / 3600).toFixed(1) }} hrs)
          </p>
          <p class="text-sm" :class="{'text-red-600': isLate, 'text-green-600': !isLate}">
            <span v-if="!isWithinWindow">You cannot start work at this time.</span>
            <span v-else-if="isLate">Starting late: 25% penalty applies.</span>
            <span v-else>On time start available.</span>
          </p>
        </div>
        <div class="flex justify-between">
          <button
            @click="exit"
            class="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
          >
            Exit
          </button>
          <button
            @click="startWork"
            :disabled="!isWithinWindow"
            class="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start work
          </button>
        </div>
      </div>
    </div>
  </template>
  
<script setup>
import { computed } from 'vue';
  
// Expecting a job object with start/end strings (e.g., "09:00") and a currentTime prop in seconds.
const props = defineProps({
    job: {
        type: [Object, null],
        required: true,
    },
    currentTime: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['close', 'start-job']);

const showing = defineModel('modelValue', {
    default: false,
    type: Boolean,
});
  
// Helper: convert "HH:mm" into seconds since midnight.
function timeStringToSeconds(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60;
}
  
const jobStartSeconds = computed(() => timeStringToSeconds(props.job.start));
const jobEndSeconds = computed(() => timeStringToSeconds(props.job.end));
  
// The start work button is enabled only if the currentTime is within one hour before or after the job start.
// This now accounts for multiple days by normalizing the time difference.
const isWithinWindow = computed(() => {
    const currentTimeInDay = props.currentTime % 86400; // Normalize to seconds within current day (86400 = 24 * 60 * 60)
    const jobStart = jobStartSeconds.value;
    
    // Calculate the time difference, accounting for day boundaries
    let timeDiff = currentTimeInDay - jobStart;
    
    // Handle day boundary crossings
    if (timeDiff > 43200) { // More than 12 hours difference, likely crossed midnight backwards
        timeDiff -= 86400;
    } else if (timeDiff < -43200) { // More than 12 hours difference, likely crossed midnight forwards
        timeDiff += 86400;
    }
    
    // Check if within 1 hour window (before or after)
    return Math.abs(timeDiff) <= 3600;
});
  
// Consider the start "late" if currentTime is after the job start (but still within window).
const isLate = computed(() => {
    if (!isWithinWindow.value) return false;
    
    const currentTimeInDay = props.currentTime % 86400;
    const jobStart = jobStartSeconds.value;
    
    // Calculate the time difference, accounting for day boundaries
    let timeDiff = currentTimeInDay - jobStart;
    
    // Handle day boundary crossings
    if (timeDiff > 43200) {
        timeDiff -= 86400;
    } else if (timeDiff < -43200) {
        timeDiff += 86400;
    }
    
    // Late if current time is after job start time
    return timeDiff > 0;
});
  
function startWork() {
    // If late, deduct 25% (i.e. multiply pay by 0.75). Otherwise no penalty.
    const penalty = isLate.value ? 0.75 : 1;
    emit('start-job', { penalty });
}
  
function exit() {
    emit('close');
}
</script>
  